// 📁 frontend/src/pages/AddProductPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../axios';

function AddProductPage() {
  const navigate = useNavigate();
  const admin = JSON.parse(localStorage.getItem('admin'));

  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [category_id, setCategoryId] = useState('');
  const [image, setImage] = useState(null);
  const [description, setDescription] = useState('');
  const [categories, setCategories] = useState([]);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success'); // success | error

  const [optionGroups, setOptionGroups] = useState([]);
  const [newGroupName, setNewGroupName] = useState('');

  const [products, setProducts] = useState([]);
  const [editingProductId, setEditingProductId] = useState(null);

  useEffect(() => {
    if (!admin) navigate('/admin/login');
  }, [admin, navigate]);

  useEffect(() => {
    axios.get('/api/categories')
      .then(res => setCategories(res.data))
      .catch(err => console.error('❌ فشل في جلب الفئات:', err));

    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get('/api/admin/products');
      setProducts(res.data.products || []);
    } catch (error) {
      console.error('❌ فشل في جلب المنتجات:', error);
    }
  };

  const resetForm = () => {
    setName('');
    setPrice('');
    setCategoryId('');
    setImage(null);
    setDescription('');
    setOptionGroups([]);
    setEditingProductId(null);
    setMessage('');
  };

  const handleAddGroup = () => {
    if (!newGroupName.trim()) return;
    setOptionGroups([...optionGroups, { name: newGroupName, options: [] }]);
    setNewGroupName('');
  };

  const handleRemoveGroup = (index) => {
    setOptionGroups(optionGroups.filter((_, idx) => idx !== index));
  };

  const handleAddOptionToGroup = (groupIndex, option) => {
    const updated = [...optionGroups];
    updated[groupIndex].options.push(option);
    setOptionGroups(updated);
  };

  const handleRemoveOption = (groupIndex, optionIndex) => {
    const updated = [...optionGroups];
    updated[groupIndex].options.splice(optionIndex, 1);
    setOptionGroups(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    const formData = new FormData();
    formData.append('name', name);
    formData.append('price', price);
    formData.append('category_id', category_id);
    formData.append('description', description);
    if (image) formData.append('image', image);

    const validOptionGroups = optionGroups.filter(group => group.options.length > 0);
    formData.append('option_groups', JSON.stringify(validOptionGroups));

    try {
      if (editingProductId) {
        await axios.post(`/api/products/${editingProductId}?_method=PUT`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setMessage('✅ تم تحديث المنتج بنجاح');
      } else {
        await axios.post('/api/products', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setMessage('✅ تم إضافة المنتج بنجاح');
      }
      setMessageType('success');
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('❌ فشل في العملية:', error.response?.data || error.message);
      setMessage('❌ حدث خطأ أثناء حفظ المنتج');
      setMessageType('error');
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('⚠️ هل أنت متأكد من حذف هذا المنتج؟')) return;
    try {
      await axios.delete(`/api/admin/products/${id}`);
      fetchProducts();
      alert('✅ تم حذف المنتج بنجاح');
    } catch (error) {
      console.error('❌ فشل في الحذف:', error);
      alert('❌ فشل في الحذف');
    }
  };

  const handleEditProduct = async (id) => {
    try {
      const res = await axios.get(`/api/admin/products/${id}`);
      const product = res.data.product;
      setName(product.name);
      setPrice(product.price);
      setCategoryId(product.category_id);
      setDescription(product.description);
      setOptionGroups(product.option_groups || []);
      setImage(null);
      setEditingProductId(id);
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('❌ فشل في جلب بيانات المنتج:', error);
      alert('❌ فشل في تحميل المنتج للتعديل');
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>{editingProductId ? '✏️ تعديل منتج' : '➕ إضافة منتج جديد'}</h2>

      {message && (
        <div style={{
          ...styles.messageBox,
          backgroundColor: messageType === 'success' ? '#d1e7dd' : '#f8d7da',
          color: messageType === 'success' ? '#0f5132' : '#842029',
        }}>
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} style={styles.form}>

        <label>📦 الاسم:</label>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} required style={styles.input} />

        <label>💰 السعر:</label>
        <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} required style={styles.input} />

        <label>🗂️ الفئة:</label>
        <select value={category_id} onChange={(e) => setCategoryId(e.target.value)} required style={styles.input}>
          <option value="">-- اختر فئة --</option>
          {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
        </select>

        <label>🖼️ صورة:</label>
        <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files[0])} style={styles.input} />

        <label>📝 وصف:</label>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} style={styles.textarea} />

        {/* الأقسام والخيارات */}
        <hr style={styles.divider} />
        <h4>⚙️ الأقسام والخيارات</h4>

        <div style={styles.optionGroupCreator}>
          <input type="text" placeholder="اسم القسم الجديد" value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} style={styles.input} />
          <button type="button" onClick={handleAddGroup} style={styles.addBtn}>➕ إضافة قسم</button>
        </div>

        {optionGroups.map((group, groupIndex) => (
          <div key={groupIndex} style={styles.groupCard}>
            <h5>{group.name}
              <button type="button" onClick={() => handleRemoveGroup(groupIndex)} style={styles.deleteBtn}>❌</button>
            </h5>
            <ul style={styles.optionList}>
              {group.options.map((opt, i) => (
                <li key={i} style={styles.optionItem}>
                  {opt.name} (+{opt.price_diff}₪)
                  <button type="button" onClick={() => handleRemoveOption(groupIndex, i)} style={styles.deleteBtnSmall}>🗑️</button>
                </li>
              ))}
            </ul>
            <AddOptionForm onAdd={(opt) => handleAddOptionToGroup(groupIndex, opt)} />
          </div>
        ))}

        <button type="submit" style={styles.submitBtn}>
          ✔️ {editingProductId ? 'تحديث المنتج' : 'حفظ المنتج'}
        </button>

        {!editingProductId && (
          <button type="button" onClick={resetForm} style={styles.resetBtn}>🔄 إعادة تعيين</button>
        )}
      </form>

      <hr style={styles.divider} />
      <h3 style={styles.subTitle}>📦 المنتجات الحالية</h3>

      <div style={styles.productCardsContainer}>
        {products.map(prod => (
          <div key={prod.id} style={styles.productCard}>
            {prod.image ? (
              <img src={`https://al-asala-restaurant.com/api/storage/${prod.image}`} alt={prod.name} style={styles.productImg} />
            ) : (
              <div style={styles.noImage}>🚫 بدون صورة</div>
            )}
            <h4>{prod.name}</h4>
            <p>{prod.price} ₪</p>
            <p style={styles.categoryStyle}>{prod.category?.name || '-'}</p>
            <div style={styles.cardActions}>
              <button onClick={() => handleEditProduct(prod.id)} style={styles.editBtn}>✏️ تعديل</button>
              <button onClick={() => handleDeleteProduct(prod.id)} style={styles.deleteBtn}>🗑️ حذف</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AddOptionForm({ onAdd }) {
  const [name, setName] = useState('');
  const [priceDiff, setPriceDiff] = useState('');

  const handleAdd = () => {
    if (!name || isNaN(priceDiff)) return;
    onAdd({ name, price_diff: parseFloat(priceDiff) });
    setName('');
    setPriceDiff('');
  };

  return (
    <div style={styles.optionForm}>
      <input type="text" placeholder="اسم الخيار" value={name} onChange={(e) => setName(e.target.value)} style={styles.input} />
      <input type="number" placeholder="فرق السعر" value={priceDiff} onChange={(e) => setPriceDiff(e.target.value)} style={styles.inputSmall} />
      <button type="button" onClick={handleAdd} style={styles.addSmallBtn}>➕</button>
    </div>
  );
}

/* 🎨 الستايل */
const styles = {
  container: {
    direction: 'rtl',
    fontFamily: "'Cairo', sans-serif",
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#3f3d56',
    marginBottom: '15px',
    textAlign: 'center',
  },
  subTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#3f3d56',
    marginBottom: '10px',
    textAlign: 'center',
  },
  messageBox: {
    padding: '12px',
    borderRadius: '6px',
    marginBottom: '15px',
    textAlign: 'center',
  },
  form: {
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
    marginBottom: '20px',
  },
  input: {
    width: '100%',
    padding: '10px',
    borderRadius: '6px',
    border: '1px solid #ccc',
    marginBottom: '12px',
  },
  textarea: {
    width: '100%',
    padding: '10px',
    minHeight: '80px',
    borderRadius: '6px',
    border: '1px solid #ccc',
    marginBottom: '12px',
  },
  addBtn: {
    backgroundColor: '#0c6efd',
    color: '#fff',
    border: 'none',
    padding: '8px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  resetBtn: {
    backgroundColor: '#6c757d',
    color: '#fff',
    border: 'none',
    padding: '10px',
    width: '100%',
    borderRadius: '6px',
    cursor: 'pointer',
    marginBottom: '10px',
  },
  submitBtn: {
    backgroundColor: '#28a745',
    color: '#fff',
    border: 'none',
    padding: '10px',
    width: '100%',
    borderRadius: '6px',
    fontSize: '15px',
    cursor: 'pointer',
    marginBottom: '10px',
  },
  divider: {
    margin: '25px 0',
  },
  optionGroupCreator: {
    display: 'flex',
    gap: '10px',
    marginBottom: '10px',
  },
  groupCard: {
    border: '1px solid #ddd',
    borderRadius: '6px',
    padding: '12px',
    marginBottom: '10px',
    backgroundColor: '#fffaf2',
  },
  optionList: {
    listStyleType: 'square',
    paddingInlineStart: '20px',
  },
  optionForm: {
    display: 'flex',
    gap: '10px',
    marginTop: '10px',
  },
  inputSmall: {
    width: '100px',
    padding: '8px',
    borderRadius: '6px',
    border: '1px solid #ccc',
  },
  deleteBtn: {
    backgroundColor: '#e63946',
    color: '#fff',
    padding: '5px 10px',
    borderRadius: '6px',
    cursor: 'pointer',
    marginRight: '8px',
  },
  deleteBtnSmall: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#e63946',
    cursor: 'pointer',
  },
  addSmallBtn: {
    backgroundColor: '#0c6efd',
    color: '#fff',
    border: 'none',
    padding: '8px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  productCardsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '15px',
  },
  productCard: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '12px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
    textAlign: 'center',
  },
  productImg: {
    width: '100%',
    height: '140px',
    objectFit: 'cover',
    borderRadius: '6px',
    marginBottom: '10px',
  },
  noImage: {
    width: '100%',
    height: '140px',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6c757d',
    marginBottom: '10px',
  },
  categoryStyle: {
    fontSize: '13px',
    color: '#6c757d',
  },
  cardActions: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginTop: '8px',
  },
  editBtn: {
    backgroundColor: '#0c6efd',
    color: '#fff',
    padding: '6px 10px',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
};

export default AddProductPage;
